/**
 * 부스 워터마크 — 사진·영상 공통으로 하단에 브랜드 이름 등을 얇은 띠로 새긴다 (BOOTH-103).
 *
 * 콘솔이 캔버스로 투명 PNG 를 그려 서버에 PATCH 하면, 키오스크는 사진에 합성하고
 * 서버 ffmpeg 는 영상에 오버레이한다 — 사진·영상 양쪽 모두 적용된다.
 * 자막(`video-caption.ts`)과 같은 구조.
 */
import { normalizeCaption, captionLength } from "./video-caption.js";
// ──────────────────────────────────────────────
// 상수
// ──────────────────────────────────────────────
/** 워터마크 텍스트 최대 길이 — grapheme 기준(이모지·결합 문자 1개 = 1자). */
export const WATERMARK_MAX_GRAPHEMES = 40;
/** 워터마크 PNG 너비(픽셀). 키오스크가 그리는 캔버스 폭. */
export const WATERMARK_PNG_W = 1440;
/** 워터마크 PNG 높이(픽셀). 사진 폭 기준 하단 띠 — 1440×160 투명 PNG 를 사진 폭에 맞춰 축소한다. */
export const WATERMARK_PNG_H = 160;
/** 기본 폰트 크기(px). 텍스트가 넘치면 4px 씩 줄인다. */
export const WATERMARK_FONT_PX = 64;
/** 폰트 크기 하한(px). 이 크기에서도 넘치면 잘린다. */
export const WATERMARK_FONT_PX_MIN = 32;
/** 좌우 여백(px). 텍스트가 이 안에 들어와야 한다. */
export const WATERMARK_MARGIN_PX = 56;
/** 텍스트 세로 중심 위치 — PNG 높이 대비. */
export const WATERMARK_TEXT_Y_RATIO = 0.56;
/** 자간(em). */
export const WATERMARK_LETTER_SPACING_EM = 0.08;
/** 워터마크 불투명도. */
export const WATERMARK_ALPHA = 0.85;
/** 워터마크 PNG dataURL 상한(디코드 바이트). */
export const WATERMARK_IMAGE_MAX_BYTES = 512 * 1024;
/** 워터마크 이미지 저장 경로 접두어. */
export const WATERMARK_KEY_PREFIX = "b/watermarks/";
// ── 스타일 옵션 (BOOTH-129) — 위치 9칸 · 글꼴 10종(콘솔 번들 OFL 서체만) · 크기 · 색 4종 · 불투명도 ──
/** 글자 크기 옵션 범위(1440 기준 px). 기본 `WATERMARK_FONT_PX`(64). */
export const WATERMARK_FONT_PX_OPTION_MIN = 24;
export const WATERMARK_FONT_PX_OPTION_MAX = 160;
export const WATERMARK_FONT_PX_STEP = 4;
/** 불투명도 옵션 범위. 기본 `WATERMARK_ALPHA`(0.85). */
export const WATERMARK_ALPHA_MIN = 0.3;
export const WATERMARK_ALPHA_MAX = 1;
export const WATERMARK_ALPHA_STEP = 0.05;
/** 띠 높이 = 글자 크기 × 2.5 — 1440×160 ↔ 64px 의 현행 비율을 유지한다. */
export const WATERMARK_BAND_RATIO = 2.5;
/** 상단 위치의 텍스트 세로 중심 — 하단 튜닝값(0.56, 가장자리에서 살짝 안쪽)을 대칭으로 뒤집은 값. 중단은 0.5. */
export const WATERMARK_TEXT_Y_RATIO_TOP = 1 - WATERMARK_TEXT_Y_RATIO;
/** 3×3 그리드 행 우선 순서. */
export const WATERMARK_POSITIONS = [
    "top-left", "top-center", "top-right",
    "middle-left", "middle-center", "middle-right",
    "bottom-left", "bottom-center", "bottom-right",
];
const JA_ZH_SERIF = '"Noto Serif JP", "Noto Serif SC", serif';
const JA_ZH_SANS = '"Noto Sans JP", "Noto Sans SC", sans-serif';
const JA_ZH_CURSIVE = '"Yomogi", "Ma Shan Zheng", cursive';
/**
 * 글꼴 10종 — 전부 콘솔 `main.tsx` 가 이미 로드하는 `@fontsource/*`(Google Fonts, SIL OFL) 만 쓴다.
 * 키오스크·서버는 PNG 만 받으므로 서체가 필요 없다.
 */
export const WATERMARK_FONTS = [
    { id: "serif-light", name: "명조 가는", stack: `"Cormorant Garamond", "Nanum Myeongjo", ${JA_ZH_SERIF}`, weight: 300, letterSpacingEm: WATERMARK_LETTER_SPACING_EM },
    { id: "serif-bold", name: "명조 굵은", stack: `"Nanum Myeongjo", ${JA_ZH_SERIF}`, weight: 700, letterSpacingEm: WATERMARK_LETTER_SPACING_EM },
    { id: "sans", name: "고딕", stack: `"Noto Sans KR", ${JA_ZH_SANS}`, weight: 300, letterSpacingEm: WATERMARK_LETTER_SPACING_EM },
    { id: "sans-bold", name: "고딕 굵은", stack: `"Noto Sans KR", ${JA_ZH_SANS}`, weight: 700, letterSpacingEm: WATERMARK_LETTER_SPACING_EM },
    { id: "rounded", name: "둥근고딕", stack: `"Gowun Dodum", "Zen Maru Gothic", "Noto Sans SC", sans-serif`, weight: 400, letterSpacingEm: WATERMARK_LETTER_SPACING_EM },
    { id: "pen", name: "손글씨", stack: `"Nanum Pen Script", ${JA_ZH_CURSIVE}`, weight: 400, letterSpacingEm: 0 },
    { id: "gamja", name: "감자꽃", stack: `"Gamja Flower", ${JA_ZH_CURSIVE}`, weight: 400, letterSpacingEm: 0 },
    { id: "brush", name: "붓글씨", stack: `"East Sea Dokdo", "Yuji Syuku", "Zhi Mang Xing", cursive`, weight: 400, letterSpacingEm: 0 },
    { id: "songmyung", name: "송명", stack: `"Song Myung", "Shippori Mincho", "ZCOOL XiaoWei", serif`, weight: 400, letterSpacingEm: WATERMARK_LETTER_SPACING_EM },
    { id: "cinzel", name: "로마 대문자 세리프 (영문)", stack: `"Cinzel", "Nanum Myeongjo", ${JA_ZH_SERIF}`, weight: 400, letterSpacingEm: WATERMARK_LETTER_SPACING_EM },
];
/** 색 4종 — hex 는 글자색, shadow 는 가독성용 그림자색(밝은 글자엔 어두운 그림자, 어두운 글자엔 밝은 그림자). */
export const WATERMARK_COLORS = {
    paper: { name: "종이흰", hex: "#F6F3ED", shadow: "rgba(23,20,17,0.45)" },
    ink: { name: "먹", hex: "#171411", shadow: "rgba(255,255,255,0.35)" },
    gold: { name: "금", hex: "#C9A96E", shadow: "rgba(23,20,17,0.45)" },
    gray: { name: "회색", hex: "#9A9A9A", shadow: "rgba(23,20,17,0.35)" },
};
/** 현행(BOOTH-103) 모습 그대로 — 우하단 · 명조 가는 · 64px · 종이흰 · 0.85. */
export const DEFAULT_WATERMARK_STYLE = {
    position: "bottom-right",
    fontId: "serif-light",
    fontPx: WATERMARK_FONT_PX,
    colorId: "paper",
    alpha: WATERMARK_ALPHA,
};
// ──────────────────────────────────────────────
// 순수 함수
// ──────────────────────────────────────────────
/**
 * 워터마크 텍스트 정규화 — 앞뒤 공백 제거, 개행·연속 공백을 한 칸으로.
 * `video-caption.ts` 의 `normalizeCaption` 과 동일한 규칙.
 */
export function normalizeWatermarkText(raw) {
    return normalizeCaption(raw);
}
/**
 * 정규화된 워터마크 텍스트가 유효한지 — 1 ~ `WATERMARK_MAX_GRAPHEMES` grapheme, 정규화된 형태여야 한다.
 */
export function isValidWatermarkText(text) {
    const n = captionLength(text);
    return n >= 1 && n <= WATERMARK_MAX_GRAPHEMES && text === normalizeWatermarkText(text);
}
/**
 * 실제 렌더에 쓸 폰트 크기(px) 계산.
 * `WATERMARK_FONT_PX`(64) 부터 4px 씩 줄여 텍스트 폭이 좌우 여백 안에 들어오는 첫 값을 반환한다.
 * 하한 `WATERMARK_FONT_PX_MIN`(32) 에 도달하면 넘쳐도 그 값으로 고정.
 *
 * @param measureAt 폰트 크기(px)를 받아 텍스트 렌더 폭(px)을 반환하는 함수 (캔버스 `measureText` 래퍼).
 */
export function watermarkFontPx(measureAt, startPx = WATERMARK_FONT_PX) {
    const maxWidth = WATERMARK_PNG_W - WATERMARK_MARGIN_PX * 2;
    // 하한 = 요청 크기의 절반(최소 16px). 기본 64 → 32 로 BOOTH-103 동작 그대로.
    const minPx = Math.max(16, Math.floor(startPx / 2));
    for (let px = startPx; px > minPx; px -= 4) {
        if (measureAt(px) <= maxWidth)
            return px;
    }
    return minPx;
}
/** 스타일 해석 — `style` 누락(옛 저장값)·필드 누락은 기본으로 채운다. */
export function resolveWatermarkStyle(w) {
    return { ...DEFAULT_WATERMARK_STYLE, ...(w?.style ?? {}) };
}
const FONT_IDS = new Set(WATERMARK_FONTS.map((f) => f.id));
const POSITION_IDS = new Set(WATERMARK_POSITIONS);
const EPS = 1e-6;
/** 서버 PATCH 검증·콘솔 방어용 — enum 멤버십, fontPx 정수·범위·STEP 배수, alpha 범위(소수 오차 허용). */
export function isValidWatermarkStyle(x) {
    if (!x || typeof x !== "object" || Array.isArray(x))
        return false;
    const s = x;
    if (typeof s.position !== "string" || !POSITION_IDS.has(s.position))
        return false;
    if (typeof s.fontId !== "string" || !FONT_IDS.has(s.fontId))
        return false;
    if (typeof s.colorId !== "string" || !(s.colorId in WATERMARK_COLORS))
        return false;
    const px = s.fontPx;
    if (typeof px !== "number" || !Number.isInteger(px) || px < WATERMARK_FONT_PX_OPTION_MIN || px > WATERMARK_FONT_PX_OPTION_MAX)
        return false;
    if ((px - WATERMARK_FONT_PX_OPTION_MIN) % WATERMARK_FONT_PX_STEP !== 0)
        return false;
    const a = s.alpha;
    if (typeof a !== "number" || !Number.isFinite(a) || a < WATERMARK_ALPHA_MIN - EPS || a > WATERMARK_ALPHA_MAX + EPS)
        return false;
    return true;
}
/** 모르는 id 는 기본 글꼴(serif-light). */
export function watermarkFontById(id) {
    return WATERMARK_FONTS.find((f) => f.id === id) ?? WATERMARK_FONTS[0];
}
/** 띠(PNG) 높이 — 폭은 항상 `WATERMARK_PNG_W`. 64 → 160. */
export function watermarkBandHeight(fontPx) {
    return Math.round(fontPx * WATERMARK_BAND_RATIO);
}
/** 위치 id → 가로·세로 정렬. */
export function positionAlign(p) {
    const [v, h] = p.split("-");
    return { h, v };
}
/**
 * PNG 안 텍스트 기준점 — 좌/중/우는 x·textAlign, 세로는 상 0.44 · 중 0.5 · 하 0.56(현행) 비율.
 * 콘솔 렌더러 전용(키오스크·서버는 PNG 만 얹는다).
 */
export function watermarkTextAnchor(style, pngH) {
    const { h, v } = positionAlign(style.position);
    const x = h === "left" ? WATERMARK_MARGIN_PX : h === "center" ? WATERMARK_PNG_W / 2 : WATERMARK_PNG_W - WATERMARK_MARGIN_PX;
    const ratio = v === "top" ? WATERMARK_TEXT_Y_RATIO_TOP : v === "middle" ? 0.5 : WATERMARK_TEXT_Y_RATIO;
    return { x, y: pngH * ratio, align: h };
}
/**
 * 사진 위 워터마크 오버레이 영역.
 * 사진 폭 기준으로 띠 PNG 를 비례 축소하고 스타일의 세로 위치(상/중/하)에 붙인다 — 키오스크 캔버스와 서버 ffmpeg 가 같은 함수를 쓴다.
 * 기본 스타일이면 BOOTH-103 과 동일: `h = photoW * 160 / 1440`, `y = photoH - h`.
 */
export function watermarkOverlayRect(photoW, photoH, style = DEFAULT_WATERMARK_STYLE) {
    // 띠 높이는 사진 폭 비례. 사진이 띠보다 낮으면(명함 + 160px) 띠를 사진 높이로 클램프 — 글자는 축소되지 않고 띠만 꽉 찬다.
    const h = Math.min(photoH, photoW * watermarkBandHeight(style.fontPx) / WATERMARK_PNG_W);
    const { v } = positionAlign(style.position);
    const y = v === "top" ? 0 : v === "middle" ? (photoH - h) / 2 : photoH - h;
    return { x: 0, y, w: photoW, h };
}
/**
 * 워터마크 이미지 공개 URL 에서 저장소 키를 추출한다.
 * 유효한 경우(우리 호스트 + `b/watermarks/` 경로, `boothId` 지정 시 해당 폴더) 에만 키를 반환.
 * 잘못된 URL 이면 null — 절대 throw 하지 않는다.
 *
 * @param url          검사할 공개 URL
 * @param assetBaseUrl 저장소 베이스 URL (호스트 비교에만 사용)
 * @param boothId      지정 시 `b/watermarks/{boothId}/` 로 시작해야 함
 */
export function watermarkKeyFromPublicUrl(url, assetBaseUrl, boothId) {
    try {
        // URL 파싱 — 런타임(Node / 브라우저)에 모두 존재하지만 lib: ES2022 에는 선언이 없어 any 캐스팅
        const Url = globalThis.URL;
        const parsed = new Url(url);
        const base = new Url(assetBaseUrl);
        // 호스트(오리진) 비교
        if (parsed.origin !== base.origin)
            return null;
        // 경로에서 앞 슬래시 제거
        const path = parsed.pathname.startsWith("/") ? parsed.pathname.slice(1) : parsed.pathname;
        // b/watermarks/ 로 시작해야 함
        if (!path.startsWith(WATERMARK_KEY_PREFIX))
            return null;
        // boothId 가 주어지면 b/watermarks/{boothId}/ 로 시작해야 함
        if (boothId !== undefined) {
            if (!path.startsWith(`${WATERMARK_KEY_PREFIX}${boothId}/`))
                return null;
        }
        return path;
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=watermark.js.map